import type { ComponentProps, ReactNode } from "react"
import { useId } from "react"
import { cn } from "../lib/utils"
import { Field, FieldLabel } from "../components/ui/field"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select"
import FieldDescriptionOrError from "./FieldDescriptionOrError"

type Props<TValue extends string> = Omit<
  ComponentProps<typeof Select<TValue>>,
  "onChange" | "items"
> &
  Pick<ComponentProps<typeof SelectValue>, "placeholder"> &
  Pick<ComponentProps<typeof SelectTrigger>, "size"> &
  Pick<ComponentProps<typeof Field>, "orientation"> & {
    label?: string
    onChange?: (value: TValue) => void
    options: {
      label: ReactNode
      value: TValue
    }[]
    /** Sizes the field wrapper; the trigger stretches to fill it */
    className?: string
    triggerClassName?: string
    fetchMore?: ReactNode
    helperText?: string
    error?: string | null
    isError?: boolean
    contentProps?: Omit<ComponentProps<typeof SelectContent>, "key">
  }

export default function SelectField<TValue extends string>({
  label,
  placeholder,
  onChange,
  options,
  className,
  triggerClassName,
  fetchMore,
  name,
  error,
  isError = false,
  helperText,
  value,
  contentProps,
  orientation,
  required,
  ...props
}: Props<TValue>) {
  const handleChange = (value: TValue | null) => {
    if (value === null) {
      return
    }

    onChange?.(value)
  }

  const triggerId = useId()

  return (
    <Field
      className={className}
      orientation={orientation}
      data-invalid={!!error || isError}
    >
      {label && (
        <FieldLabel
          htmlFor={triggerId}
          className={cn(required && "after:content-['*']")}
        >
          {label}
        </FieldLabel>
      )}
      <Select
        onValueChange={handleChange}
        name={name}
        required={required}
        {...props}
        value={value}
        items={options}
      >
        <SelectTrigger
          id={triggerId}
          className={triggerClassName}
          aria-invalid={!!error || isError}
          // The combobox role takes no name from its contents, so an
          // unlabelled trigger is announced as just "combobox". Fall back to
          // the placeholder, which is what the field is asking for.
          aria-label={
            !label && typeof placeholder === "string" ? placeholder : undefined
          }
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <FieldDescriptionOrError description={helperText} error={error} />
        <SelectContent {...contentProps}>
          <SelectGroup>
            {options.map(({ label, value }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
            {fetchMore}
          </SelectGroup>
        </SelectContent>
      </Select>
    </Field>
  )
}
